import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {MessageContext} from 'src/contexts/message-context';
import {useEntitlementNamespaces} from 'src/hooks/dictionaries';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {
    REMOVE_RESOURCE_MODAL_WIDTH,
    RemoveResourceModal,
} from 'src/modules/manage-entitlements/update-resource/remove-resource-modal';
import {getResourcesWithoutRelations} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Table} from '@pb/kit-component-table';


import {replace} from '../../entitlement-drawer.slice';

import {NotAssociatedResourceRow} from './not-associated-resource-row';

import type {OpenRequest} from 'src/modules/entitlements/components/drawer/typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ResourceList} from 'src/openapi/admin/models/ResourceList';
import type {ServerResponse} from 'src/typedef';

type Props = {
    currentEntitlement: OpenRequest,
};

export const NotAssociatedResourcesTable = ({currentEntitlement}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage} = useContext(MessageContext);

    const namespace = useMemo(() => (
        currentEntitlement?.notAssociatedEntitlementsDetails?.namespace || ''
    ), [currentEntitlement]);

    const pagination = useMemo(() => (
        currentEntitlement?.notAssociatedEntitlementsDetails?.pagination || {currentPage: 1, pageSize: 10}
    ), [currentEntitlement]);

    const {namespaces} = useEntitlementNamespaces();
    const [resourceForDeletion, setResourceForDeletion] = useState<EntitlementResource | null>(null);

    const [response, setResponse] = useState<ServerResponse<ResourceList> | null>(null);

    const loadNotAssociatedResources = useCallback(async () => {
        if (namespace) {
            setResponse(null);
            const serverResponse = await apiDispatch(getResourcesWithoutRelations({
                pageNumber: pagination.currentPage,
                pageSize: pagination.pageSize,
                namespace,
            }));

            if (serverResponse.payload.success) {
                setResponse({data: serverResponse.payload.data});
            } else {
                setResponse({error: serverResponse.payload.data});
            }
        }
    }, [pagination, setResponse, apiDispatch, namespace]);

    useEffect(() => {
        loadNotAssociatedResources();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prevDeps, currentDeps) => {
        const {pagination: prevPagination, namespace: prenNamespace} = prevDeps;
        const {
            loadNotAssociatedResources: currentLoad,
            pagination: currentPagination,
            namespace: currentNamespace,
        } = currentDeps;

        const paginationChanged =
            prevPagination.currentPage !== currentPagination.currentPage ||
            prevPagination.pageSize !== currentPagination.pageSize;

        const namespaceChanged = prenNamespace !== currentNamespace;

        if (paginationChanged || namespaceChanged) {
            currentLoad();
        }
    }, {loadNotAssociatedResources, pagination, namespace});

    const onChangePageSize = useCallback((_currentPage: number, pageSize: number) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: {currentPage: 1, pageSize},
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    const closeDeleteResourceModal = useCallback(() => {
        setResourceForDeletion(null);
    }, [setResourceForDeletion]);

    const onRemove = useCallback(() => {
        if (response?.data?.totalCount) {
            const totalPages = Math.ceil((response.data.totalCount - 1) / pagination.pageSize);

            if ((response.data.totalCount - 1) && pagination.currentPage > totalPages) {
                if (currentEntitlement?.notAssociatedEntitlementsDetails) {
                    dispatch(replace({
                        ...currentEntitlement,
                        notAssociatedEntitlementsDetails: {
                            ...currentEntitlement.notAssociatedEntitlementsDetails,
                            pagination: {currentPage: totalPages, pageSize: pagination.pageSize},
                        },
                    }));
                }
            } else {
                loadNotAssociatedResources();
            }
        }

        setResourceForDeletion(null);
        setSuccessMessage('Resource removed successfully');
    }, [
        response,
        pagination,
        setResourceForDeletion,
        setSuccessMessage,
        dispatch,
        loadNotAssociatedResources,
        currentEntitlement,
    ]);

    const setNamespace = useCallback((value: string) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    namespace: value,
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    const onPaginationChange = useCallback((newPagination: Pagination) => {
        if (currentEntitlement?.notAssociatedEntitlementsDetails) {
            dispatch(replace({
                ...currentEntitlement,
                notAssociatedEntitlementsDetails: {
                    ...currentEntitlement.notAssociatedEntitlementsDetails,
                    pagination: newPagination,
                },
            }));
        }
    }, [dispatch, currentEntitlement]);

    return (
        <ServerResponseHandler serverError={response?.error || null} smallView={true}>
            <Group direction="v" alignItems="start">
                <b>Resources not associated with any grant</b>
                <div style={{width: 470}}>
                    <Label>Namespace</Label>
                    <ComboBox
                        value={namespace}
                        onChange={setNamespace}
                        name="namespace"
                        placeholder="Select namespace">
                        {namespaces.map(({code, description}) => (
                            <ComboboxItem key={code} value={code} caption={description}/>
                        ))}
                    </ComboBox>
                </div>
                {namespace ? (
                    <BoxLoading loading={!response}>
                        <Group direction="v" alignItems="start">
                            <Table borderedGrid={true}>
                                <Head>
                                    <Cell width={56}><strong>#</strong></Cell>
                                    <Cell>Resource ({response?.data?.totalCount})</Cell>
                                    <Cell width={50}/>
                                </Head>
                                <Body>
                                    {response?.data?.items?.map((resource, index) => (
                                        <NotAssociatedResourceRow
                                            key={resource.id}
                                            resource={resource}
                                            index={index}
                                            pagination={pagination}
                                            setResourceForDeletion={setResourceForDeletion}
                                        />
                                    ))}
                                </Body>
                            </Table>
                            <PaginationStripe
                                pageSize={pagination.pageSize}
                                totalCount={response?.data?.totalCount || 0}
                                currentPage={pagination.currentPage}
                                onPaginationChange={onPaginationChange}
                                onChangePaginationSize={onChangePageSize}
                                sizeSwitcherPosition="LEFT"
                                sizeSwitcherType="ONLY_PAGE_SIZE"
                                sizes={[5, 10]}
                            />
                        </Group>
                    </BoxLoading>
                ) : null}
                <Dialog
                    shown={!!resourceForDeletion}
                    setShown={closeDeleteResourceModal}
                    wrapperProps={{style: {width: REMOVE_RESOURCE_MODAL_WIDTH}}}>
                    {resourceForDeletion ? (
                        <RemoveResourceModal
                            resourceId={resourceForDeletion.id}
                            resourceName={resourceForDeletion.name}
                            onClose={closeDeleteResourceModal}
                            onDone={onRemove}
                        />
                    ) : null}
                </Dialog>
            </Group>
        </ServerResponseHandler>
    );
};
